import React, { useRef } from "react";

import { FigureModalSource, inferFigureTextFromPath } from "./figure-modal-context";
import { handleImagePaste, isAllowedImageType } from "./paste-image";

function ManuscriptFigureModal({
    activeFigureData,
    figureDraft,
    figureModalSource,
    isFigureUploadPending,
    isImageBinary,
    projectFigureFiles,
    selectedNode,
    onClose,
    onDelete,
    onApply,
    onSourceChange,
    onDraftChange,
    onUploadFile,
}) {
    const fileInputRef = useRef(null);

    if (!figureDraft) {
        return null;
    }

    const updateDraft = (patch) => {
        onDraftChange((current) => ({
            ...current,
            ...patch,
        }));
    };

    const handlePathChange = (event) => {
        const nextPath = event.target.value;
        const inferred =
            figureModalSource === FigureModalSource.FROM_URL || figureModalSource === FigureModalSource.FILE_TREE
                ? inferFigureTextFromPath(nextPath, figureDraft)
                : {};

        updateDraft({
            path: nextPath,
            ...inferred,
        });
    };

    const handleProjectFilePick = (path) => {
        updateDraft({
            path,
            ...inferFigureTextFromPath(path, figureDraft),
        });
    };

    const handleUploadDrop = async (event) => {
        event.preventDefault();
        if (isFigureUploadPending) {
            return;
        }

        const file = event.dataTransfer?.files?.[0];
        if (!file || !isAllowedImageType(file.type)) {
            return;
        }

        await onUploadFile?.(file);
    };

    return (
        <div
            className="manuscript-figure-modal-overlay"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="manuscript-figure-modal-card">
                <div className="manuscript-figure-modal-header">
                    <div className="manuscript-figure-modal-title">Figure</div>
                    <button type="button" className="manuscript-figure-modal-button" onClick={onClose}>
                        닫기
                    </button>
                </div>
                <div className="manuscript-figure-modal-body">
                    <div className="manuscript-figure-modal-field">
                        <div className="manuscript-figure-modal-label">Source</div>
                        <div className="manuscript-figure-modal-source-grid">
                            <button
                                type="button"
                                className={`manuscript-figure-modal-source-button${
                                    figureModalSource === FigureModalSource.FILE_TREE ? " active" : ""
                                }`}
                                disabled={isFigureUploadPending}
                                onClick={() => {
                                    onSourceChange(FigureModalSource.FILE_TREE);
                                    if (isImageBinary) {
                                        updateDraft({
                                            path:
                                                selectedNode?.path_cache ||
                                                selectedNode?.name ||
                                                figureDraft?.path ||
                                                "",
                                        });
                                    }
                                }}
                            >
                                Project
                            </button>
                            <button
                                type="button"
                                className={`manuscript-figure-modal-source-button${
                                    figureModalSource === FigureModalSource.FROM_URL ? " active" : ""
                                }`}
                                disabled={isFigureUploadPending}
                                onClick={() => onSourceChange(FigureModalSource.FROM_URL)}
                            >
                                URL
                            </button>
                            <button
                                type="button"
                                className={`manuscript-figure-modal-source-button${
                                    figureModalSource === FigureModalSource.FILE_UPLOAD ? " active" : ""
                                }`}
                                disabled={isFigureUploadPending}
                                onClick={() => onSourceChange(FigureModalSource.FILE_UPLOAD)}
                            >
                                Upload
                            </button>
                        </div>
                    </div>

                    <div className="manuscript-figure-modal-field">
                        <div className="manuscript-figure-modal-label">
                            {figureModalSource === FigureModalSource.FROM_URL
                                ? "Image URL / Path"
                                : figureModalSource === FigureModalSource.FILE_UPLOAD
                                  ? "Uploaded File Name"
                                  : "Project File Path"}
                        </div>
                        <input
                            className="manuscript-figure-modal-input"
                            value={figureDraft?.path || ""}
                            list={
                                figureModalSource === FigureModalSource.FILE_TREE
                                    ? "manuscript-figure-project-files"
                                    : undefined
                            }
                            onChange={handlePathChange}
                        />
                        {figureModalSource === FigureModalSource.FILE_TREE ? (
                            <datalist id="manuscript-figure-project-files">
                                {(projectFigureFiles || []).map((file) => (
                                    <option key={file.id || file.path} value={file.path}>
                                        {file.name || file.path}
                                    </option>
                                ))}
                            </datalist>
                        ) : null}
                        {figureModalSource === FigureModalSource.FILE_TREE && !isImageBinary ? (
                            <div className="manuscript-figure-modal-value">
                                현재 선택된 이미지 파일이 없습니다. 파일 트리에서 이미지 노드를 선택한 상태에서 열면
                                경로가 자동으로 채워집니다.
                            </div>
                        ) : null}
                        {figureModalSource === FigureModalSource.FILE_TREE && (projectFigureFiles || []).length > 0 ? (
                            <div className="manuscript-figure-modal-file-list">
                                {(projectFigureFiles || []).slice(0, 8).map((file) => (
                                    <button
                                        key={file.id || file.path}
                                        type="button"
                                        className="manuscript-figure-modal-file-item"
                                        onClick={() => handleProjectFilePick(file.path)}
                                    >
                                        <span className="manuscript-figure-modal-file-name">
                                            {file.name || file.path}
                                        </span>
                                        <span className="manuscript-figure-modal-file-path">{file.path}</span>
                                    </button>
                                ))}
                            </div>
                        ) : null}
                        {figureModalSource === FigureModalSource.FILE_UPLOAD ? (
                            <>
                                <div
                                    className="manuscript-figure-modal-upload-dropzone"
                                    onDragOver={(event) => event.preventDefault()}
                                    onDrop={handleUploadDrop}
                                >
                                    <div className="manuscript-figure-modal-upload-title">
                                        파일을 드롭하거나 선택해서 업로드
                                    </div>
                                    <div className="manuscript-figure-modal-upload-subtitle">
                                        PNG, JPG, SVG, PDF, PS 형식을 지원합니다.
                                    </div>
                                    {figureDraft?.path ? (
                                        <div className="manuscript-figure-modal-upload-current">{figureDraft.path}</div>
                                    ) : null}
                                </div>
                                <div className="manuscript-figure-modal-value">
                                    붙여넣기, 드롭, 파일 선택 중 어느 경로로 업로드해도 완료되면 프로젝트 파일 경로로
                                    전환됩니다.
                                </div>
                                <div className="manuscript-figure-modal-upload-actions">
                                    <button
                                        type="button"
                                        className="manuscript-figure-modal-button"
                                        onClick={async () => {
                                            await handleImagePaste();
                                        }}
                                        disabled={isFigureUploadPending}
                                    >
                                        클립보드 이미지 붙여넣기
                                    </button>
                                    <button
                                        type="button"
                                        className="manuscript-figure-modal-button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={isFigureUploadPending}
                                    >
                                        {isFigureUploadPending ? "업로드 중..." : "파일 선택"}
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        hidden
                                        accept="image/*,.pdf,.ps,.eps"
                                        onChange={async (event) => {
                                            const file = event.target.files?.[0];
                                            if (file) {
                                                await onUploadFile?.(file);
                                            }
                                            event.target.value = "";
                                        }}
                                    />
                                </div>
                            </>
                        ) : null}
                    </div>

                    <div className="manuscript-figure-modal-field">
                        <label className="manuscript-figure-modal-checkbox-row">
                            <input
                                type="checkbox"
                                checked={Boolean(figureDraft?.includeCaption)}
                                onChange={(event) => updateDraft({ includeCaption: event.target.checked })}
                            />
                            <span>Caption</span>
                        </label>
                        {figureDraft?.includeCaption ? (
                            <input
                                className="manuscript-figure-modal-input"
                                value={figureDraft?.captionText || ""}
                                onChange={(event) => updateDraft({ captionText: event.target.value })}
                            />
                        ) : null}
                    </div>

                    <div className="manuscript-figure-modal-field">
                        <label className="manuscript-figure-modal-checkbox-row">
                            <input
                                type="checkbox"
                                checked={Boolean(figureDraft?.includeLabel)}
                                onChange={(event) => updateDraft({ includeLabel: event.target.checked })}
                            />
                            <span>Label</span>
                        </label>
                        {figureDraft?.includeLabel ? (
                            <input
                                className="manuscript-figure-modal-input"
                                value={figureDraft?.labelText || ""}
                                onChange={(event) => updateDraft({ labelText: event.target.value })}
                            />
                        ) : null}
                    </div>

                    <div className="manuscript-figure-modal-field">
                        <div className="manuscript-figure-modal-label">Width</div>
                        <input
                            className="manuscript-figure-modal-input"
                            value={figureDraft?.width ?? ""}
                            onChange={(event) => updateDraft({ width: event.target.value })}
                        />
                    </div>

                    {activeFigureData ? (
                        <div className="manuscript-figure-modal-field">
                            <div className="manuscript-figure-modal-label">Range</div>
                            <div className="manuscript-figure-modal-value">
                                {activeFigureData.from}-{activeFigureData.to}
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="manuscript-figure-modal-footer">
                    {activeFigureData ? (
                        <button type="button" className="manuscript-figure-modal-button" onClick={onDelete}>
                            삭제
                        </button>
                    ) : null}
                    <button type="button" className="manuscript-figure-modal-button" onClick={onClose}>
                        취소
                    </button>
                    <button
                        type="button"
                        className="manuscript-figure-modal-button manuscript-figure-modal-button-primary"
                        onClick={onApply}
                        disabled={isFigureUploadPending || !String(figureDraft?.path || "").trim()}
                    >
                        적용
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ManuscriptFigureModal;
